import { ComponentPropsWithoutRef, Dispatch, SetStateAction } from 'react';

import './TextArea.scss';

interface CommonTextAreaProps {
  /**
   * If this textarea is rendered in a list, a unique suffix for each textarea can be
   * specified here to ensure unique ids.
   */
  idSuffix?: string;
  /**
   * The visual styling of the textarea. Use value 'none' for an unstyled look (will fall back to whatever base style is
   * defined). Default value is 'default'.
   */
  visualType?: 'default' | 'none';
}

type StateTextAreaProps = Omit<ComponentPropsWithoutRef<'textarea'>, 'value' | 'onChange'> &
  CommonTextAreaProps & {
    state:
      | [string | undefined, Dispatch<SetStateAction<string | undefined>>]
      | [string, Dispatch<SetStateAction<string>>];
  };

type OnChangeTextAreaProps = ComponentPropsWithoutRef<'textarea'> & CommonTextAreaProps;

type TextAreaProps = StateTextAreaProps | OnChangeTextAreaProps;

export default function TextArea(props: TextAreaProps) {
  const { idSuffix, id, className = '', visualType = 'default', ...rest } = props;
  const fullId = props.id ? (idSuffix ? id + idSuffix : props.id) : undefined;
  const fullClassName = `text-area ${visualType !== 'none' ? `text-area-${visualType}` : ''} ${className}`;

  if ('state' in rest) {
    const { state, ...restStateProps } = rest;
    const [stateValue, stateSetter] = state;

    return (
      <textarea
        id={fullId}
        value={stateValue}
        onChange={event => stateSetter(event.target.value)}
        className={fullClassName}
        {...restStateProps}
      />
    );
  } else {
    return <textarea id={fullId} className={fullClassName} {...rest} />;
  }
}
